import React, { useCallback, useRef, useState } from "react";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import { deleteEvent } from "repositories/scheduler-repository";
import { encodeId, handleError, handleSuccess } from "utils";
import constants from "constants";
import {
  buildSimpleDeleteEventPayload,
  shouldSkipDeleteOptionsScreen,
} from "./eventsWorkspaceDelete.utils";

/**
 * Card-level Delete for Event Workspace views.
 * Skips the multi-option DeleteEvents page when it would not offer choices
 * (single staff or unassigned, non-repeat) and shows a standard confirm modal instead.
 *
 * @param {{
 *   t: Function,
 *   history: { push: Function },
 *   effectiveOwnerIdString?: string,
 *   onSuccess?: () => void,
 * }} options
 */
export function useSimpleEventCardDelete({
  t,
  history,
  effectiveOwnerIdString,
  onSuccess,
}) {
  const [confirmState, setConfirmState] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);

  const navigateToDeleteOptions = useCallback(
    (event, { requestedDate, lockSelectedUsers, selectedUserIdsFromStaffView }) => {
      history.push({
        pathname: constants.routes.scheduler.deleteEvent.replace(
          ":eventId",
          encodeId(event.id),
        ),
        state: {
          requestedDate,
          selectedUserIdsFromStaffView: selectedUserIdsFromStaffView || [],
          lockSelectedUsers: Boolean(lockSelectedUsers),
        },
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, effectiveOwnerIdString],
  );

  /**
   * @param {object} event
   * @param {string} requestedDate YYYY-MM-DD
   * @param {{
   *   lockSelectedUsers?: boolean,
   *   selectedUserIdsFromStaffView?: Array<number|string>,
   * }} [options]
   */
  const handleCardDeleteClick = useCallback(
    (event, requestedDate, options = {}) => {
      if (!event?.id || !requestedDate || isDeletingRef.current) return;

      const lockSelectedUsers = Boolean(
        options.lockSelectedUsers ?? event?.userId,
      );
      const selectedUserIdsFromStaffView =
        options.selectedUserIdsFromStaffView ??
        (event?.userId != null ? [event.userId] : []);

      if (shouldSkipDeleteOptionsScreen(event)) {
        setConfirmState({
          event,
          requestedDate,
          lockSelectedUsers,
          selectedUserIdsFromStaffView,
        });
        return;
      }

      navigateToDeleteOptions(event, {
        requestedDate,
        lockSelectedUsers,
        selectedUserIdsFromStaffView,
      });
    },
    [navigateToDeleteOptions],
  );

  const closeConfirmModal = useCallback(() => {
    if (isDeletingRef.current) return;
    setConfirmState(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!confirmState?.event?.id || isDeletingRef.current) return;

    isDeletingRef.current = true;
    setIsDeleting(true);
    try {
      const res = await deleteEvent(buildSimpleDeleteEventPayload(confirmState));
      handleSuccess(res?.message);
      setConfirmState(null);
      onSuccess?.();
    } catch (error) {
      handleError(error?.message || error);
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  }, [confirmState, onSuccess]);

  const eventOrShiftLabel = confirmState?.event?.isShift
    ? t("accountOwner.shiftTab")
    : t("accountOwner.eventTab");

  const renderSimpleDeleteConfirmModal = useCallback(
    () => (
      <DeleteConfirmationModal
        confirmDelete={Boolean(confirmState)}
        closeDeleteModal={closeConfirmModal}
        confirmDeleteMessage={confirmDelete}
        title={t("scheduler.deleteEvent", { eventOrShift: eventOrShiftLabel })}
        deleteMessage={t("scheduler.deleteEventShiftConfirmQuestion")}
        btnText={t("delete")}
        isLoading={isDeleting}
      />
    ),
    [
      closeConfirmModal,
      confirmDelete,
      confirmState,
      eventOrShiftLabel,
      isDeleting,
      t,
    ],
  );

  return {
    handleCardDeleteClick,
    renderSimpleDeleteConfirmModal,
    isDeleting,
  };
}
